// VerDatosCompras.js
// Panel de autoservicio para que una supervisora vea su propio gasto por alumno
// (semanal, comparado con la media de todas las supervisoras) y el reparto de
// ese gasto por categoría de producto. Reutiliza los mismos endpoints que el
// panel de admin (`/api/analisis/gasto-semanal` y `/api/analisis/gasto-semanal-categorias`);
// el backend restringe automáticamente a la propia supervisora cuando el rol es
// 'supervisora', así que aquí no hace falta (ni se puede) pedir datos de otra.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../../context/authContext";

const CATEGORY_COLORS = {
  Snacks: "#b1d319",
  "Básico": "#0eb956",
  "Proteína": "#ef553b",
  Limpieza: "#636efa",
  "Sin etiquetar": "#9ca3af",
};

const LINE_COLOR = "#2563eb";
const MEAN_COLOR = "#111827";

function formatDate(d) {
  if (!d) return "";
  const t = new Date(d);
  if (isNaN(t)) return String(d);
  return t.toLocaleDateString("es-ES", { day: "2-digit", month: "short" });
}

// Gráfica de líneas hecha a mano en SVG (sin dependencias externas): una línea
// para la propia supervisora y otra (negra) para la media de todas.
const LineChart = ({ weeks, propia, media }) => {
  const containerRef = useRef(null);
  const [width, setWidth] = useState(600);
  const [hoverIdx, setHoverIdx] = useState(null); // índice de semana bajo el ratón/dedo
  const height = 260;
  const margin = { top: 16, right: 16, bottom: 46, left: 48 };
  const innerH = height - margin.top - margin.bottom;

  useEffect(() => {
    const handle = () => {
      const w = containerRef.current ? containerRef.current.clientWidth : window.innerWidth || 360;
      setWidth(Math.max(320, Math.min(900, w)));
    };
    handle();
    window.addEventListener("resize", handle);
    return () => window.removeEventListener("resize", handle);
  }, []);

  const virtualWidth = Math.max(width, Math.min(1200, 90 + weeks.length * 60));
  const innerW = Math.max(260, virtualWidth - margin.left - margin.right);

  const allValues = [
    ...(propia || []).filter((v) => v != null),
    ...(media || []).filter((v) => v != null),
  ];
  const maxY = allValues.length ? Math.max(...allValues) : 1;

  const x = (i) => margin.left + (weeks.length > 1 ? (i / (weeks.length - 1)) * innerW : innerW / 2);
  const y = (v) => margin.top + innerH - ((v || 0) / Math.max(0.0001, maxY)) * innerH;

  const pathFor = (values) => {
    const segments = [];
    let seg = [];
    values.forEach((v, i) => {
      if (v == null) {
        if (seg.length) { segments.push(seg); seg = []; }
      } else {
        seg.push({ i, v });
      }
    });
    if (seg.length) segments.push(seg);
    return segments.map((s) => s.map((p, idx) => `${idx === 0 ? "M" : "L"} ${x(p.i)} ${y(p.v)}`).join(" "));
  };

  // ancho de cada "columna" de semana, para las zonas invisibles de hover/tap
  const colWidth = weeks.length > 1 ? innerW / (weeks.length - 1) : innerW;

  const formatVal = (v) => (v == null ? "—" : `${v.toFixed(2)} €`);

  return (
    <div ref={containerRef} style={{ width: "100%", overflowX: "auto" }}>
      <svg
        width={virtualWidth}
        height={height}
        style={{ background: "#fff", borderRadius: 10 }}
        onMouseLeave={() => setHoverIdx(null)}
      >
        {[0, 0.25, 0.5, 0.75, 1].map((t, i) => {
          const val = t * maxY;
          const yy = y(val);
          return (
            <g key={i}>
              <line x1={margin.left} x2={virtualWidth - margin.right} y1={yy} y2={yy} stroke="#f1f5f9" />
              <text x={margin.left - 8} y={yy + 4} fontSize={11} textAnchor="end" fill="#64748b">{val ? val.toFixed(0) : "0"}</text>
            </g>
          );
        })}

        {pathFor(media || []).map((d, i) => (
          <path key={`media-${i}`} d={d} fill="none" stroke={MEAN_COLOR} strokeWidth={3} strokeOpacity={0.9} />
        ))}
        {pathFor(propia || []).map((d, i) => (
          <path key={`propia-${i}`} d={d} fill="none" stroke={LINE_COLOR} strokeWidth={2.5} />
        ))}
        {(propia || []).map((v, i) => v == null ? null : (
          <circle key={`p-${i}`} cx={x(i)} cy={y(v)} r={3.5} fill={LINE_COLOR} stroke="#fff" strokeWidth={1} />
        ))}

        {weeks.map((w, i) => (
          <text key={w} x={x(i)} y={height - margin.bottom + 16} fontSize={10.5} textAnchor="middle" fill={hoverIdx === i ? "#111827" : "#334155"} fontWeight={hoverIdx === i ? 800 : 400}>
            {formatDate(w)}
          </text>
        ))}

        {/* línea guía + resaltado de los puntos de la semana bajo el ratón/dedo */}
        {hoverIdx != null && (
          <g pointerEvents="none">
            <line x1={x(hoverIdx)} x2={x(hoverIdx)} y1={margin.top} y2={height - margin.bottom} stroke="#cbd5e1" strokeWidth={1.5} strokeDasharray="4 3" />
            {media[hoverIdx] != null && <circle cx={x(hoverIdx)} cy={y(media[hoverIdx])} r={5} fill={MEAN_COLOR} stroke="#fff" strokeWidth={1.5} />}
            {propia[hoverIdx] != null && <circle cx={x(hoverIdx)} cy={y(propia[hoverIdx])} r={5.5} fill={LINE_COLOR} stroke="#fff" strokeWidth={1.5} />}
          </g>
        )}

        {/* zonas invisibles (una por semana) que detectan el ratón encima o el tap */}
        {weeks.map((w, i) => (
          <rect
            key={`hit-${w}`}
            x={Math.max(margin.left, x(i) - colWidth / 2)}
            y={margin.top}
            width={colWidth}
            height={innerH}
            fill="transparent"
            onMouseEnter={() => setHoverIdx(i)}
            onMouseMove={() => setHoverIdx(i)}
            onClick={() => setHoverIdx((cur) => (cur === i ? null : i))}
            style={{ cursor: "pointer" }}
          />
        ))}

        {/* tooltip con los valores de esa semana */}
        {hoverIdx != null && (() => {
          const boxW = 148;
          const boxH = 62;
          let bx = x(hoverIdx) + 10;
          if (bx + boxW > virtualWidth - 4) bx = x(hoverIdx) - boxW - 10;
          const by = margin.top + 4;
          return (
            <g pointerEvents="none">
              <rect x={bx} y={by} width={boxW} height={boxH} rx={8} fill="#1e293b" fillOpacity={0.95} />
              <text x={bx + 10} y={by + 18} fontSize={11.5} fontWeight={800} fill="#fff">{formatDate(weeks[hoverIdx])}</text>
              <text x={bx + 10} y={by + 36} fontSize={11.5} fill={LINE_COLOR} fontWeight={700}>Tú: {formatVal(propia[hoverIdx])}</text>
              <text x={bx + 10} y={by + 52} fontSize={11.5} fill="#e2e8f0" fontWeight={700}>Media: {formatVal(media[hoverIdx])}</text>
            </g>
          );
        })()}
      </svg>
    </div>
  );
};

// Pie ligero con CSS (conic-gradient) + leyenda con porcentajes; evita depender
// de librerías de gráficos que no están instaladas en este proyecto.
const PieConLeyenda = ({ labels, values, colors, size = 180 }) => {
  const total = values.reduce((a, b) => a + b, 0);
  if (!total) return <div style={{ padding: 20, textAlign: "center", color: "#94a3b8" }}>Sin datos</div>;
  let acc = 0;
  const stops = values.map((v, i) => {
    const desde = (acc / total) * 100;
    acc += v;
    const hasta = (acc / total) * 100;
    return `${colors[i]} ${desde}% ${hasta}%`;
  }).join(", ");
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 20, flexWrap: "wrap", justifyContent: "center" }}>
      <div style={{
        width: size, height: size, borderRadius: "50%", flexShrink: 0,
        background: `conic-gradient(${stops})`, boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.08)",
      }} />
      <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
        {labels.map((l, i) => (
          <div key={l} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13.5, fontWeight: 700, color: "#334155" }}>
            <span style={{ width: 12, height: 12, borderRadius: 3, background: colors[i], display: "inline-block", flexShrink: 0 }} />
            {l} — {((values[i] / total) * 100).toFixed(0)}%
          </div>
        ))}
      </div>
    </div>
  );
};

const VerDatosCompras = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const currentUser = (user && (user.user || user)) || "";

  const [analisisData, setAnalisisData] = useState(null);
  const [categoriasData, setCategoriasData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!currentUser) return;
    let cancelado = false;
    setLoading(true);
    setErr(null);
    Promise.all([
      axios.get("/api/analisis/gasto-semanal"),
      axios.get("/api/analisis/gasto-semanal-categorias"),
    ])
      .then(([rAnal, rCat]) => {
        if (cancelado) return;
        setAnalisisData(rAnal.data || { weeks: [], series: [], mean: { values: [] } });
        setCategoriasData(rCat.data || { weeks: [], series: {} });
      })
      .catch((e) => {
        if (cancelado) return;
        setErr(e.response?.data?.error || "Error cargando datos");
        setAnalisisData(null);
        setCategoriasData(null);
      })
      .finally(() => { if (!cancelado) setLoading(false); });
    return () => { cancelado = true; };
  }, [currentUser]);

  // Serie propia (el backend, siendo supervisora, ya solo devuelve la suya, pero
  // se busca por nombre por si acaso para no depender de que sea la única).
  const serieValues = useMemo(() => {
    if (!analisisData || !Array.isArray(analisisData.series)) return [];
    const s = analisisData.series.find((x) => x.name === currentUser) || analisisData.series[0];
    return s ? s.values : [];
  }, [analisisData, currentUser]);

  const mediaValues = useMemo(() => {
    if (!analisisData || !analisisData.mean || !Array.isArray(analisisData.mean.values)) return [];
    return analisisData.mean.values;
  }, [analisisData]);

  const weeks = analisisData?.weeks || [];

  const promedioSemanal = useMemo(() => {
    const vals = serieValues.filter((v) => v != null);
    if (!vals.length) return null;
    return vals.reduce((a, b) => a + b, 0) / vals.length;
  }, [serieValues]);

  const mediaGeneral = useMemo(() => {
    const vals = mediaValues.filter((v) => v != null);
    if (!vals.length) return null;
    return vals.reduce((a, b) => a + b, 0) / vals.length;
  }, [mediaValues]);

  // Indicador visual (carita + color) que compara el gasto propio con la media:
  // por debajo de la media = verde sonriente; entre la media y +1€ = degradado
  // amarillo (cerca de la media) -> naranja (cerca de +1€) con cara neutra;
  // más de 1€ por encima de la media = rojo con cara triste. El texto es un
  // mensaje motivacional (sin cifras) según el tramo.
  const indicadorGasto = useMemo(() => {
    if (promedioSemanal == null || mediaGeneral == null) return null;
    const diff = promedioSemanal - mediaGeneral;
    if (diff <= 0) {
      return {
        emoji: "😄",
        bg: "#dcfce7",
        border: "#86efac",
        color: "#15803d",
        texto: "¡Vas genial!",
        subtexto: "Estás gestionando el gasto de forma ejemplar, ¡sigue así!",
      };
    }
    if (diff <= 1) {
      const t = diff / 1; // 0 = justo en la media (amarillo) · 1 = a +1€ de la media (naranja)
      const hexAMezcla = (h) => [parseInt(h.slice(1, 3), 16), parseInt(h.slice(3, 5), 16), parseInt(h.slice(5, 7), 16)];
      const amarillo = hexAMezcla("#fef08a");
      const naranja = hexAMezcla("#fb923c");
      const mezcla = amarillo.map((c, i) => Math.round(c + (naranja[i] - c) * t));
      const bordeMezcla = hexAMezcla("#facc15").map((c, i) => Math.round(c + (hexAMezcla("#f97316")[i] - c) * t));
      return {
        emoji: "😐",
        bg: `rgb(${mezcla.join(",")})`,
        border: `rgb(${bordeMezcla.join(",")})`,
        color: "#78350f",
        texto: t < 0.5 ? "Vas bien" : "Ojo, vigila el gasto",
        subtexto: t < 0.5 ? "Estás cerca del objetivo, ¡tú puedes!" : "Con un pequeño ajuste te acercas al objetivo",
      };
    }
    return {
      emoji: "😟",
      bg: "#fee2e2",
      border: "#fca5a5",
      color: "#b91c1c",
      texto: "¡Atención!",
      subtexto: "Toca revisar el gasto, seguro que lo consigues",
    };
  }, [promedioSemanal, mediaGeneral]);

  const pieSummary = useMemo(() => {
    if (!categoriasData || !Array.isArray(categoriasData.weeks) || !categoriasData.series) return null;
    const semanas = categoriasData.weeks;
    if (!semanas.length) return null;
    const claves = ["snacks", "basico", "proteina", "limpieza"];
    const etiquetas = { snacks: "Snacks", basico: "Básico", proteina: "Proteína", limpieza: "Limpieza" };
    const promedios = claves.map((k) => {
      const serie = categoriasData.series[k] || [];
      const vals = serie.filter((v) => v != null);
      return vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : 0;
    });
    const labels = claves.map((k) => etiquetas[k]).filter((_, i) => promedios[i] > 0);
    const values = promedios.filter((v) => v > 0);
    const colors = labels.map((l) => CATEGORY_COLORS[l] || "#9ca3af");
    return { labels, values, colors };
  }, [categoriasData]);

  if (loading) return <div style={{ padding: 24, textAlign: "center" }}>⏳ Cargando...</div>;

  return (
    <div style={{ minHeight: "100vh", background: "#f0f4f8", paddingBottom: 40 }}>
      <div style={{ background: "linear-gradient(135deg,#1e293b,#0f172a)", color: "#fff", padding: "14px 16px 18px", display: "flex", alignItems: "center", gap: 12 }}>
        <button onClick={() => navigate("/supervisora/compras")} style={{ background: "rgba(255,255,255,0.12)", border: "none", borderRadius: 10, color: "#fff", fontWeight: 700, fontSize: 14, cursor: "pointer", padding: "8px 14px" }}>
          ← Volver
        </button>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 11, color: "rgba(255,255,255,0.5)" }}>Mis compras</div>
          <div style={{ fontSize: 18, fontWeight: 800 }}>📊 Datos de compras — {currentUser}</div>
        </div>
      </div>

      <div style={{ padding: 16, maxWidth: 760, margin: "0 auto" }}>
        {err && (
          <div style={{ background: "#fee2e2", border: "1px solid #fca5a5", color: "#991b1b", borderRadius: 10, padding: "12px 16px", marginBottom: 16, textAlign: "center" }}>
            {err}
          </div>
        )}

        {!err && (
          <>
            {indicadorGasto && (
              <div style={{
                background: indicadorGasto.bg,
                border: `2px solid ${indicadorGasto.border}`,
                borderRadius: 20,
                padding: "20px 22px",
                marginBottom: 16,
                display: "flex",
                alignItems: "center",
                gap: 18,
                boxShadow: "0 4px 16px rgba(15,23,42,0.08)",
              }}>
                <div style={{ fontSize: 68, lineHeight: 1 }}>{indicadorGasto.emoji}</div>
                <div>
                  <div style={{ fontWeight: 800, fontSize: 19, color: indicadorGasto.color }}>{indicadorGasto.texto}</div>
                  <div style={{ fontSize: 14.5, color: indicadorGasto.color, opacity: 0.9, marginTop: 3, fontWeight: 600 }}>
                    {indicadorGasto.subtexto}
                  </div>
                </div>
              </div>
            )}
            <div style={{ background: "#fff", borderRadius: 16, padding: 16, boxShadow: "0 2px 10px rgba(15,23,42,0.05)", border: "1px solid #e2e8f0", marginBottom: 16 }}>
              <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 10, marginBottom: 12 }}>
                <div style={{ fontWeight: 800, fontSize: 15, color: "#1e293b" }}>Gasto medio semanal por alumno</div>
                <div style={{ display: "flex", gap: 16, fontSize: 13 }}>
                  <span style={{ fontWeight: 800, color: LINE_COLOR }}>Tú: {promedioSemanal != null ? `${promedioSemanal.toFixed(2)} €` : "—"}</span>
                  <span style={{ fontWeight: 800, color: MEAN_COLOR }}>Media: {mediaGeneral != null ? `${mediaGeneral.toFixed(2)} €` : "—"}</span>
                </div>
              </div>
              {weeks.length ? (
                <LineChart weeks={weeks} propia={serieValues} media={mediaValues} />
              ) : (
                <div style={{ padding: 24, textAlign: "center", color: "#94a3b8" }}>Sin datos todavía</div>
              )}
            </div>

            <div style={{ background: "#fff", borderRadius: 16, padding: 16, boxShadow: "0 2px 10px rgba(15,23,42,0.05)", border: "1px solid #e2e8f0" }}>
              <div style={{ fontWeight: 800, fontSize: 15, color: "#1e293b", marginBottom: 12 }}>Reparto del gasto por categoría</div>
              {pieSummary ? (
                <PieConLeyenda labels={pieSummary.labels} values={pieSummary.values} colors={pieSummary.colors} />
              ) : (
                <div style={{ padding: 20, textAlign: "center", color: "#94a3b8" }}>Sin datos</div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default VerDatosCompras;
